import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { specialDateOperatingHoursApi } from '@/lib/api/special-date-operating-hours.api';
import { queryKeys } from '@/lib/query-client';
import type {
  CreateSpecialDateOperatingHoursRequest,
  UpdateSpecialDateOperatingHoursRequest,
  PaginationParams,
} from '@geely/shared/types';

interface UseSpecialDateOperatingHoursOptions {
  enabled?: boolean;
}

export function useSpecialDateOperatingHours(storeId: string | undefined, options: UseSpecialDateOperatingHoursOptions = {}) {
  const { enabled = true } = options;

  return useQuery({
    queryKey: queryKeys.specialDateOperatingHours(storeId || ''),
    queryFn: async () => {
      const response = await specialDateOperatingHoursApi.list({ storeId: storeId! });
      return response.data;
    },
    enabled: enabled && !!storeId,
  });
}

export function useSpecialDateOperatingHoursPaginated(
  storeId: string | undefined,
  params: Required<PaginationParams>,
) {
  return useQuery({
    queryKey: queryKeys.specialDateOperatingHoursPaginated(storeId || '', params),
    queryFn: async () => {
      const response = await specialDateOperatingHoursApi.listPaginated({
        storeId: storeId!,
        ...params,
      });
      return response.data;
    },
    enabled: !!storeId,
    placeholderData: keepPreviousData,
  });
}

export function useCreateSpecialDateOperatingHours() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateSpecialDateOperatingHoursRequest) => {
      const response = await specialDateOperatingHoursApi.create(data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.specialDateOperatingHours(variables.storeId),
      });
    },
  });
}

export function useUpdateSpecialDateOperatingHours() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
      storeId,
    }: {
      id: string;
      data: UpdateSpecialDateOperatingHoursRequest;
      storeId: string;
    }) => {
      const response = await specialDateOperatingHoursApi.update(id, data);
      return { data: response.data, storeId };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.specialDateOperatingHours(variables.storeId),
      });
    },
  });
}

export function useDeleteSpecialDateOperatingHours() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, storeId }: { id: string; storeId: string }) => {
      await specialDateOperatingHoursApi.delete(id);
      return { storeId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.specialDateOperatingHours(result.storeId),
      });
    },
  });
}
